import type { ComponentProps, ReactNode } from 'react';
import { FieldInfo, hasFieldError, useFieldContext } from './shared';
import { useFormReadOnly } from './form-read-only';
import { Label } from '../label';
import { Switch } from '../switch';
import { cn } from '../../lib/utils';

export default function SwitchField({
	label,
	description,
	className,
	size,
	variant,
	orientation,
	position,
	...props
}: ComponentProps<'button'> & {
	label?: ReactNode;
	description?: ReactNode;
	size?: 'lg';
	variant?: 'primary';
	orientation?: 'vertical' | 'horizontal';
	position?: 'left' | 'right';
}) {
	const field = useFieldContext<boolean>();
	const formReadOnly = useFormReadOnly();
	const isReadOnly = props.disabled ?? formReadOnly;
	const isHorizontal = orientation === 'horizontal';
	const isRight = position === 'right';

	const switchEl = (
		<Switch
			id={field.name}
			name={field.name}
			aria-invalid={hasFieldError(field.state.meta)}
			checked={field.state.value}
			onBlur={field.handleBlur}
			onCheckedChange={field.handleChange}
			size={size}
			variant={variant}
			{...props}
			disabled={isReadOnly}
			className={cn(isReadOnly && 'opacity-100')}
		/>
	);

	const labelEl = (label || description) && (
		<div className="flex flex-col gap-0.5">
			{label && (
				<Label
					data-slot="form-label"
					data-error={field.state.meta.isTouched && !field.state.meta.isValid}
					className={cn(
						'data-[error=true]:text-destructive leading-tight cursor-pointer',
						isHorizontal && 'w-36 shrink-0 text-sm',
						size === 'lg' && 'text-lg',
					)}
					htmlFor={field.name}
				>
					{label}
				</Label>
			)}
			{description && <p className="text-muted-foreground text-xs leading-tight">{description}</p>}
		</div>
	);

	return (
		<div>
			<div className={cn('flex items-center gap-3', isRight && 'justify-between', className)}>
				{isRight ? (
					<>
						{labelEl}
						{switchEl}
					</>
				) : (
					<>
						{switchEl}
						{labelEl}
					</>
				)}
			</div>
			<FieldInfo fieldState={field.state} />
		</div>
	);
}
